import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  FaCheckCircle,
  FaExclamationCircle,
  FaUniversity,
  FaSpinner,
  FaSave,
  FaArrowLeft,
  FaTrash,
  FaEdit,
  FaStar,
  FaPlusCircle
} from "react-icons/fa";

const API_URL = process.env.REACT_APP_API_URL;

const bankFields: Array<any> = [
  {
    label: "Bank Name",
    name: "bank_name",
    required: true,
    placeholder: "e.g. HDFC Bank"
  },
  { label: "Branch", name: "branch", placeholder: "e.g. Connaught Place" },
  {
    label: "Account Number",
    name: "account_number",
    required: true,
    placeholder: "e.g. 50100123456789"
  },
  { label: "IFSC Code", name: "ifsc_code", placeholder: "e.g. HDFC0000123" },
  { label: "MICR Code", name: "micr_code", placeholder: "e.g. 110240001" },
  { label: "SWIFT Code", name: "swift_code", placeholder: "e.g. HDFCINBB" },
  { label: "Account Type", name: "account_type", placeholder: "e.g. Savings / Current" },
  { label: "Account Holder Name", name: "account_holder_name", placeholder: "e.g. Aadvik Tech Services" },
];

const initialBankState = {
  id: "",
  bank_name: "",
  branch: "",
  account_number: "",
  ifsc_code: "",
  micr_code: "",
  swift_code: "",
  account_type: "",
  account_holder_name: "",
  company_id: "",
  is_default: 0
};

const AddBank: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const companyId = searchParams.get("company_id");

  const [company, setCompany] = useState<any>(null);
  const [banksList, setBanksList] = useState<any[]>([]);
  const [bank, setBank] = useState(initialBankState);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [isEdit, setIsEdit] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const alertRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (successMsg || errorMsg) {
      alertRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [successMsg, errorMsg]);

  // Load company details and bank details list
  const loadData = async () => {
    if (!companyId) {
      setErrorMsg("Missing company ID in URL.");
      return;
    }
    setLoading(true);
    setErrorMsg("");
    try {
      // 1. Fetch company details
      const companyRes = await axios.get(`${API_URL}/auth/company/${companyId}`);
      setCompany(companyRes.data);

      // 2. Fetch all bank details
      const banksRes = await axios.get(`${API_URL}/auth/company/${companyId}/bank`);
      setBanksList(Array.isArray(banksRes.data) ? banksRes.data : []);
    } catch (err: any) {
      console.error(err);
      setErrorMsg("Unable to load bank and company details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [companyId]);

  const handleBankInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setBank((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setBank((prev) => ({ ...prev, [name]: checked ? 1 : 0 }));
  };

  const handleEditClick = (selectedBank: any) => {
    setBank({ ...selectedBank, company_id: companyId });
    setIsEdit(true);
    setShowForm(true);
    setErrorMsg("");
    setSuccessMsg("");
  };

  const handleAddNewClick = () => {
    setBank({ ...initialBankState, company_id: companyId ?? "" });
    setIsEdit(false);
    setShowForm(true);
    setErrorMsg("");
    setSuccessMsg("");
  };

  const handleSetDefault = async (bankId: string | number) => {
    setErrorMsg("");
    setSuccessMsg("");
    try {
      await axios.put(`${API_URL}/auth/bank/${bankId}/default`);
      setSuccessMsg("Default bank account updated successfully.");
      await loadData();
    } catch (err: any) {
      setErrorMsg(err.response?.data?.error || "Failed to set default bank.");
    }
  };

  const handleDelete = async (bankId: string | number) => {
    if (!window.confirm("Are you sure you want to delete this bank account?")) return;
    setErrorMsg("");
    setSuccessMsg("");
    try {
      await axios.delete(`${API_URL}/auth/bank/${bankId}`);
      setSuccessMsg("Bank details deleted successfully.");
      await loadData();
      if (bank.id === bankId) {
        setShowForm(false);
      }
    } catch (err: any) {
      setErrorMsg(err.response?.data?.error || "Failed to delete bank details.");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");

    if (!bank.bank_name.trim() || !bank.account_number.trim()) {
      setErrorMsg("Bank name and account number are required.");
      return;
    }

    setSubmitting(true);
    try {
      if (isEdit && bank.id) {
        // Update (PUT)
        await axios.put(
          `${API_URL}/auth/bank/${bank.id}`,
          {
            ...bank,
            company_id: companyId,
          }
        );
        setSuccessMsg("Bank details updated successfully.");
      } else {
        // Create (POST)
        await axios.post(
          `${API_URL}/auth/company/${companyId}/bank`,
          { ...bank, company_id: companyId }
        );
        setSuccessMsg("Bank details added successfully.");
      }
      setShowForm(false);
      setBank(initialBankState);
      await loadData();
    } catch (err: any) {
      if (err.response?.data?.error) {
        setErrorMsg(err.response.data.error);
      } else {
        setErrorMsg("Failed to save bank details.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-full bg-gradient-to-br from-[#f8fafc] via-[#f1f5f9] to-[#e2e8f0] p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header Block */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-extrabold text-[#1e293b] tracking-tight flex items-center gap-3">
              <FaUniversity className="text-indigo-600" /> Bank Accounts
            </h1>
            <p className="text-slate-500 mt-1 font-medium">
              Manage multiple bank accounts for <span className="text-indigo-600 font-bold">{company?.company_name || "Company"}</span>
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/company")}
              className="px-4 py-2 border border-slate-200 rounded-xl bg-white hover:bg-slate-50 text-slate-700 shadow-sm font-semibold text-sm flex items-center gap-2 transition"
            >
              <FaArrowLeft /> Back to Companies
            </button>
            {!showForm && (
              <button
                onClick={handleAddNewClick}
                className="px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white shadow-md font-bold text-sm flex items-center gap-2 transition"
              >
                <FaPlusCircle /> Add Bank Account
              </button>
            )}
          </div>
        </div>

        {/* Messaging Container */}
        <div ref={alertRef} className="mb-6">
          {errorMsg && <Alert type="error" message={errorMsg} />}
          {successMsg && <Alert type="success" message={successMsg} />}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
          {/* Bank details List */}
          <div className={`${showForm ? "lg:col-span-2" : "lg:col-span-3"} space-y-6`}>
            {loading ? (
              <div className="flex flex-col justify-center items-center py-20 text-indigo-600 font-semibold animate-pulse text-lg bg-white rounded-2xl border border-slate-200/60 shadow-sm">
                <FaSpinner className="animate-spin text-3xl mb-3" />
                Loading bank records...
              </div>
            ) : banksList.length === 0 ? (
              <div className="text-center py-20 bg-white border border-dashed border-slate-300 rounded-2xl p-8 shadow-sm flex flex-col items-center">
                <FaUniversity className="text-5xl text-slate-300 mb-4" />
                <h3 className="text-lg font-bold text-slate-700">No bank accounts added yet</h3>
                <p className="text-slate-400 max-w-sm mt-1 mb-6 text-sm">
                  Add one or more bank accounts to print on company invoices. You can select one as the default.
                </p>
                <button
                  onClick={handleAddNewClick}
                  className="px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm shadow transition"
                >
                  Create First Bank Account
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {banksList.map((item) => (
                  <div
                    key={item.id}
                    className={`bg-white border rounded-2xl p-6 shadow-sm flex flex-col justify-between transition-all duration-300 hover:shadow-md relative overflow-hidden ${
                      item.is_default ? "border-emerald-500 ring-2 ring-emerald-500/10" : "border-slate-200/80"
                    }`}
                  >
                    {item.is_default ? (
                      <div className="absolute top-0 right-0 bg-emerald-500 text-white text-[11px] font-extrabold px-3 py-1 rounded-bl-xl flex items-center gap-1 shadow-sm uppercase tracking-wider">
                        <FaStar className="text-[10px]" /> Default
                      </div>
                    ) : null}

                    <div>
                      <h3 className="text-lg font-bold text-[#1e293b] flex items-center gap-2 mb-2 pr-16">
                        {item.bank_name}
                      </h3>
                      <p className="text-xs text-slate-400 font-semibold mb-4 uppercase tracking-wider">{item.account_type || "General Account"}</p>
                      
                      <div className="space-y-2 text-sm text-slate-600 font-medium">
                        <div className="flex justify-between border-b border-slate-50 pb-1.5">
                          <span className="text-slate-400">A/c Holder:</span>
                          <span className="font-semibold text-slate-800">{item.account_holder_name || company?.company_name}</span>
                        </div>
                        <div className="flex justify-between border-b border-slate-50 pb-1.5">
                          <span className="text-slate-400">Account No:</span>
                          <span className="font-bold text-[#0f172a] font-mono">{item.account_number}</span>
                        </div>
                        <div className="flex justify-between border-b border-slate-50 pb-1.5">
                          <span className="text-slate-400">IFSC Code:</span>
                          <span className="font-semibold text-slate-800 font-mono">{item.ifsc_code || "N/A"}</span>
                        </div>
                        <div className="flex justify-between border-b border-slate-50 pb-1.5">
                          <span className="text-slate-400">Branch:</span>
                          <span className="text-slate-700">{item.branch || "N/A"}</span>
                        </div>
                        {item.swift_code && (
                          <div className="flex justify-between border-b border-slate-50 pb-1.5">
                            <span className="text-slate-400">SWIFT:</span>
                            <span className="font-semibold text-slate-800 font-mono">{item.swift_code}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between gap-3">
                      {!item.is_default ? (
                        <button
                          onClick={() => handleSetDefault(item.id)}
                          className="text-xs font-bold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 px-3.5 py-1.5 rounded-lg transition"
                        >
                          Make Default
                        </button>
                      ) : (
                        <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-3 py-1 rounded-lg">
                          Active billing bank
                        </span>
                      )}

                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleEditClick(item)}
                          className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                          title="Edit Details"
                        >
                          <FaEdit className="text-base" />
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition"
                          title="Delete Account"
                        >
                          <FaTrash className="text-sm" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Form Block (Add/Edit bank details) */}
          {showForm && (
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-md animate__animated animate__fadeIn">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3 mb-5">
                <h2 className="text-lg font-bold text-[#1e293b] flex items-center gap-2">
                  <FaUniversity className="text-indigo-600" />
                  {isEdit ? "Edit Account" : "Add Account"}
                </h2>
                <button
                  onClick={() => setShowForm(false)}
                  className="text-slate-400 hover:text-slate-600 text-sm font-bold"
                >
                  Cancel
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
                {bankFields.map((fld: any) => (
                  <div key={fld.name} className="flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                      {fld.label} {fld.required && <span className="text-rose-500">*</span>}
                    </label>
                    <input
                      type="text"
                      name={fld.name}
                      value={(bank as any)[fld.name] || ""}
                      onChange={handleBankInputChange}
                      placeholder={fld.placeholder}
                      className="px-4 py-2 text-sm text-slate-800 rounded-xl bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition placeholder-slate-400 font-medium"
                      required={fld.required}
                    />
                  </div>
                ))}

                <div className="flex items-center gap-2 py-2">
                  <input
                    type="checkbox"
                    id="is_default"
                    name="is_default"
                    checked={bank.is_default === 1}
                    onChange={handleCheckboxChange}
                    className="w-4 h-4 rounded text-indigo-600 border-slate-300 focus:ring-indigo-500"
                  />
                  <label htmlFor="is_default" className="text-sm font-semibold text-slate-600 cursor-pointer select-none">
                    Set as Default Account
                  </label>
                </div>

                <button
                  type="submit"
                  className="w-full py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm shadow-md flex items-center justify-center gap-2 transition"
                  disabled={submitting}
                >
                  {submitting ? (
                    <FaSpinner className="animate-spin inline" />
                  ) : (
                    <FaSave />
                  )}
                  {submitting ? "Saving..." : isEdit ? "Update Details" : "Save Account"}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Alert: React.FC<{ type: "success" | "error"; message: string }> = ({
  type,
  message,
}) => {
  const icon =
    type === "success" ? (
      <FaCheckCircle className="mr-2 text-green-600" />
    ) : (
      <FaExclamationCircle className="mr-2 text-red-600" />
    );
  const color =
    type === "success"
      ? "bg-green-50 text-green-700 border-green-200"
      : "bg-red-50 text-red-700 border-red-200";
  return (
    <div
      className={`rounded-xl border px-4 py-3 flex items-center shadow-sm font-medium ${color}`}
    >
      {icon}
      {message}
    </div>
  );
};

export default AddBank;